import React from 'react'
import { Box, Checkbox, Tooltip, makeStyles } from '@material-ui/core'
import { CheckCircle } from '@material-ui/icons'
import { CommissionCase } from '../types'
import { conceptAuditLabel, conceptKey, money, typeLabel } from '../utils'

const useStyles = makeStyles({
  commission: { minWidth: 360 },
  segments: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 6,
    width: 350,
    marginBottom: 8
  },
  segment: {
    minHeight: 40,
    borderRadius: 5,
    padding: '5px 7px',
    border: '1px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  segmentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.5rem'
  },
  segmentLabel: { fontSize: 11, fontWeight: 700, lineHeight: 1.2 },
  segmentAmount: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums',
    marginTop: 0
  },
  paid: {
    backgroundColor: '#eef8f1',
    borderColor: '#b9ddc3',
    color: '#237a3b'
  },
  pending: {
    backgroundColor: '#fff7ed',
    borderColor: '#f2c99f',
    color: '#a94700'
  },
  missing: {
    backgroundColor: '#f2f3f5',
    borderColor: '#e0e3e7',
    color: '#8a919a'
  },
  conceptCheckbox: { padding: 2, margin: -2 },
  commissionTotal: {
    color: '#252a31',
    fontSize: 14,
    fontVariantNumeric: 'tabular-nums'
  },
  '@media (max-width: 600px)': { commission: { minWidth: 330 } }
})

interface Props {
  row: CommissionCase
  admin: boolean
  selected: Set<string>
  onToggle: (key: string) => void
}

const ConceptDisplay: React.FC<Props> = ({ row, admin, selected, onToggle }) => {
  const classes = useStyles()
  return (
    <Box className={classes.commission}>
      <Box className={classes.segments}>
        {Object.values(row.concepts).map(concept => {
          const key = conceptKey(row.id, concept.type)
          const stateLabel =
            concept.state === 'paid' ? 'Pagada' : concept.state === 'pending' ? 'Pendiente' : 'No causada'
          const auditLabel = conceptAuditLabel(concept.eventDate, concept.eventPeriod, concept.officePeriod)
          const tooltip = [typeLabel[concept.type], money(concept.amount), stateLabel, auditLabel]
            .filter(Boolean)
            .join(' · ')
          return (
            <Tooltip key={concept.type} title={tooltip}>
              <Box
                className={`${classes.segment} ${
                  concept.state === 'paid'
                    ? classes.paid
                    : concept.state === 'pending'
                    ? classes.pending
                    : classes.missing
                }`}
              >
                <Box className={classes.segmentHeader}>
                  <span className={classes.segmentLabel}>{typeLabel[concept.type]}</span>
                  {admin && concept.selectable ? (
                    <Checkbox
                      className={classes.conceptCheckbox}
                      size="small"
                      checked={selected.has(key)}
                      onChange={() => onToggle(key)}
                      inputProps={{
                        'aria-label': `Incluir ${typeLabel[concept.type]}`
                      }}
                    />
                  ) : concept.state === 'paid' ? (
                    <CheckCircle fontSize="small" />
                  ) : null}
                </Box>
                <span className={classes.segmentAmount}>{money(concept.amount)}</span>
              </Box>
            </Tooltip>
          )
        })}
      </Box>
      <strong className={classes.commissionTotal}>Total causado: {money(row.causedTotal)}</strong>
    </Box>
  )
}

export default ConceptDisplay
